import React, {useEffect} from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Waiting from './Waiting';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import clsx from 'clsx';
import useStyles from '../styles/Styles'

import _ from 'lodash';
import { isTrue, describeDate, formatMemberAddress } from '../utils';

import { fetchInvoice } from '../actions';

const Invoice = ({invoiceId, isSm, isMd, flexLayout, buttonTop}) => {
	
	const dispatch = useDispatch();
	const classes = useStyles();
	
	const invoice = useSelector(state => state.invoices.data[invoiceId]);
	const invoiceInfo = useSelector(state => state.invoices.singles[invoiceId]);
	const logo = useSelector(state => state.requests.logo);
	const invoiceSettings = useSelector(state => state.settings.invoices.data);
	const generalSettings = useSelector(state => state.settings.general.data);
	const member = useSelector(state => state.member.data);
	
	// invoiceId isn't going to change - not a dependency
	useEffect(() => {
		dispatch(fetchInvoice(invoiceId)); // eslint-disable-next-line
	}, [dispatch])
	
	if(_.isNil(invoiceInfo)){
		return <></>
	} else if(invoiceInfo.submitting){
		return  <Waiting message='Fetching invoice detail. Please wait...' />
	} else if(invoiceInfo.error){
		return <Typography paragraph>{invoiceInfo.error}</Typography>
	} else {
		const address = formatMemberAddress(member);

		const fields = {
			course_code: 'Code', 
			name: 'Name', 
			start_date: 'Start date', 
			status: 'Status',
			amount: 'Fee'
		}
		
		const formatField = (type, value) => {
			switch(type){
				case 'start_date':
					return describeDate(value);
				case 'amount':
					return '$' + value;
				default:
					return value;
			}
		}

		/*
		Request by Andrew and Kevin to remove notes about waitlist. 
		Commenting out for now: Delete in future version,
		assuming we don't get complaints and get asked to put it back in!
		const hasWaitlist = _.some(invoice.line_items, item => {
			return item.status === 'Waitlist' && _.toNumber(item.amount) > 0;
		});
		const hasDeleted = _.some(invoice.line_items, item => {
			return item.status === 'Deleted' && _.toNumber(item.amount) > 0
		});
		*/
		
		let other_methods = [];
		if(invoiceSettings.accept_eftpos_at_office){
			other_methods.push('EFTPOS at the office')
		}
		if(invoiceSettings.accept_cheques_at_office){
			other_methods.push('Cheque at the office');
		}
		if(invoiceSettings.accept_cash_at_office){
			other_methods.push('Cash at the office');
		}
		if(invoiceSettings.accept_cheques_via_post){
			other_methods.push('Cheque by mail, sent to:\r\n' + generalSettings.formal_name + '\r\n' + invoiceSettings.postal_address);
		}

		return <>
		<Grid container className={flexLayout ? classes.invoiceHeader : null}>
			<Grid container item direction='row'>
				<Grid item xs={12} sm={flexLayout? 12 : 7} md={8}>
				<Typography variant='h3' paragraph>{invoiceSettings.use_tax_invoices ? 'TAX ' : '' }INVOICE</Typography>
				</Grid>
				<Grid item xs={12} sm={flexLayout? 12 : 5} md={4}>
				<img src={logo.image} 
					alt={generalSettings.display_name+' logo'}
					className={classes.u3aLogo} 
				/>
				</Grid>
			</Grid>
			<Grid container item direction='row'>
				<Grid item xs={12} sm={flexLayout? 12 : 7} md={8}>
				<Typography variant='body1' gutterBottom><strong>Invoice ID: </strong>{member.member_no}-{invoice.invoice_no}</Typography>
				<Typography variant='body1' paragraph><strong>Date: </strong>{describeDate(invoice.date)}</Typography>
				<Typography variant='body1'><strong>Bill to:</strong></Typography>
				<Typography variant='body1'>{member.full_name}</Typography>
				<Typography variant='body1' gutterBottom>Member no: {member.member_no}</Typography>
				<Typography variant='body1' gutterBottom>{address}</Typography>
				{member.email ? <Typography variant='body1'>E: {member.email.trim()}</Typography> : null}
				{member.mobile ? <Typography variant='body1'>Mob: {member.mobile}</Typography> : null}
				</Grid>
				<Grid item xs={12} sm={flexLayout? 12 : 5} md={4}>
				<Typography variant="body1" gutterBottom><strong>{generalSettings.formal_name}</strong></Typography>
				<Typography variant="body1">Incorporation: {invoiceSettings.incorporation}</Typography>
				<Typography variant="body1" gutterBottom>ABN: {invoiceSettings.abn}</Typography>
				<Typography variant="body1" gutterBottom>{invoiceSettings.address}</Typography>
				<Typography variant='body1'><strong>Postal address:</strong></Typography>
				<Typography variant="body1" paragraph>{invoiceSettings.postal_address}</Typography>
				</Grid>
			</Grid>
		</Grid>
		
		<Table className={classes.cartTable} size={isMd ? "medium" : "small"}>
		{flexLayout ? null :
		<TableHead>
			<TableRow>
			{_.map(fields, (f, k) => (
				<TableCell key={k}>
					{f}
				</TableCell>
			))}
			</TableRow>
		</TableHead>
		}
		<TableBody className={flexLayout ? classes.flexTable : null}>
			{_.map(invoice.line_items, i => (
				<TableRow key={i.course_code} className={i.status === 'Waitlist' ? classes.waitlistItem : null}>
				{_.map(fields, (f, k) => (
					<TableCell key={k}>
						{flexLayout ? <strong>{fields[k]}: </strong> : null}{formatField(k, i[k])}
					</TableCell>
				))}
				</TableRow>
			))}
			<TableRow className={clsx(classes.footerRow, classes.thickTop)}>
			<TableCell colSpan={1}></TableCell>
			<TableCell colSpan={3} align="right">Total{invoiceSettings.use_tax_invoices ? ' price including GST' : '' }</TableCell>
			<TableCell>${invoice.total}</TableCell>
			</TableRow>
			<TableRow className={classes.footerRow}>
			<TableCell colSpan={3}></TableCell>
			<TableCell align="right">Paid</TableCell>
			<TableCell>${invoice.paid}</TableCell>
			</TableRow>
			<TableRow className={clsx(classes.footerRow, classes.thickTop)}>
			<TableCell colSpan={3}></TableCell>
			<TableCell align="right">Due</TableCell>
			<TableCell>${invoice.due}</TableCell>
			</TableRow>
		</TableBody>
		</Table>
		{invoice.due > 0 ?
		<Grid container spacing={isMd ? 3 : 1}>
		{invoiceSettings.paypal.accept ?
		<Grid item xs={12} md={3}>
		<Typography variant="body1" gutterBottom>
		<strong>PayPal or credit card:</strong>
		</Typography>
		<Typography variant="body2" paragraph>
		Use the PayPal button {buttonTop ? 'above' : 'at the bottom of'} this invoice. 
		You can use a credit card even if you don't have a PayPal account.
		</Typography>
		</Grid>
		: null}
		{isTrue(invoiceSettings.bank.accept) && (!_.isEmpty(invoiceSettings.bank.detail) || invoiceSettings.bank.use_payid) ? 
		<Grid item xs={12} md={4}>
		<Typography variant="body1" gutterBottom>
		<strong>Bank transfer:</strong>
		</Typography>
		{invoiceSettings.bank.use_payid ? 
		<>
		<Typography variant="body1" gutterBottom>
		{invoiceSettings.bank.payid_detail}<br/>
		Reference: {invoice.member_no}-{invoice.invoice_no}
		</Typography>
		<Typography variant="body2" paragraph>
		PayID is safer than BSB/account number. Check with your financial institution to find out how to use PayID.
		</Typography>
		</> : 
		<Typography variant="body1" paragraph>
		{invoiceSettings.bank.detail}<br/>
		Use reference: {invoice.member_no}-{invoice.invoice_no}
		</Typography>
		}
		</Grid>
		: null}
		{_.isEmpty(other_methods) ? null :
		<Grid item xs={12} md={5} className={classes.condensedList}>
		<Typography variant="body1" gutterBottom>
		<strong>Other payment methods:</strong>
		</Typography>
		<ul>
		{_.map(other_methods, (m, k) => {
			return <li key={k}>
			{m}
			</li>
		})}
		</ul>
		{(invoiceSettings.accept_cheques_at_office || invoiceSettings.accept_cheques_via_post) ?
		<>If paying by cheque, please make it out to <strong>{invoiceSettings.make_cheques_out_to}</strong>, and write <strong>Invoice ID {invoice.member_no}-{invoice.invoice_no}</strong> on the back.</>
		: null}
		</Grid>}
		</Grid>
		: null}
		</>
		
	}
};
	
export default Invoice